export const HOURS_PER_DAY = 24;
export const MINUTES_PER_HOUR = 60;
export const SECONDS_PER_MINUTE = 60;
export const MILLISECONDS_PER_SECOND = 1000;
export const SECONDS_PER_DAY = HOURS_PER_DAY * MINUTES_PER_HOUR * SECONDS_PER_MINUTE;
export const MILLISECONDS_PER_DAY = SECONDS_PER_DAY * MILLISECONDS_PER_SECOND;

enum DURATION_UNIT {
  DAY = 'Day',
  MONTH = 'Month',
  YEAR = 'Year',
}

export function addDays(date: Date, daysToAdd: number) {
  return new Date(date.getTime() + (MILLISECONDS_PER_DAY * daysToAdd));
}

/**
 * Converts a UTC date string to a local date object.
 *
 * @param utcDate
 */
export function getUTCAsLocalDate(utcDate: string): Date {
  if (!utcDate.endsWith('UTC')) {
    utcDate = utcDate.concat(' ', 'UTC');
  }

  return new Date(utcDate.replace(/-/g, "/"));
}

/**
 * Returns a date for a timestamp in format MM/DD/YY or YYYY-MM-DD HH:MM:SS.
 *
 * @param timestamp
 * @param isFullDate
 */
export function getDateFromTimestamp(timestamp: number | string, isFullDate = false): string {
  const date: Date = new Date(Number(timestamp) * 1000);
  if (!isFullDate) {
    return formatToMMDDYY(date);
  }

  const offset: number = date.getTimezoneOffset() * 60 * 1000;
  const localDate: Date = new Date(date.getTime() - offset);
  return localDate.toISOString().substring(0, 19).replace('T', ' ');
}

/**
 * Formats a date object to MM/DD/YY
 * If the month or day can be represented by 1 digit,
 * then the format will change accordingly
 * 
 * E.g: 2/1/24, 11/2/24, 11/15/24
 *
 * @param date
 */
export function formatToMMDDYY(date: Date) {
  return date.toLocaleDateString('en-US', {month: 'numeric', day: 'numeric', year: '2-digit'});
}

/**
 * Formats a date using verbose format (e.g., 'January 1, 2024')
 *
 * @param date
 */
export function formatDateToVerbose(date: Date) {
  return date.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'});
}

/**
 * Calculates the number of days from a given start date based on a specified duration and duration unit.
 * 
 * If the duration unit is "day", it simply returns the duration.
 * If the duration unit is "month" or "year", it calculates the end date by adding the duration
 * to the start date and returns the difference in days between the start and end dates.
 * 
 * @param duration - The amount of time to add (e.g., '2')
 * @param duration_unit - The unit of the duration (e.g., DURATION_UNIT.DAY, DURATION_UNIT.MONTH, DURATION_UNIT.YEAR)
 * @param date - The start date in the format 'YYYY-MM-DD'
 * @returns The number of days from the start date to the calculated end date as a string
 * @throws Error if an invalid duration unit is provided
 */
export function calculateDaysFromDate(duration: string, duration_unit: DURATION_UNIT, date: string): string {
  if (!duration || !duration_unit || !date) {
    return '';
  }
  
  const startDate = new Date(date);
  let endDate: Date;

  if (duration_unit === DURATION_UNIT.DAY) {
    return duration;
  } else if (duration_unit === DURATION_UNIT.MONTH) {
    endDate = new Date(startDate);
    endDate.setMonth(startDate.getMonth() + parseInt(duration, 10));
  } else if (duration_unit === DURATION_UNIT.YEAR) {
    endDate = new Date(startDate);
    endDate.setFullYear(startDate.getFullYear() + parseInt(duration, 10));
  } else {
    throw new Error('Invalid duration unit');
  }

  return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)).toString();
}
